/**
 * EPLAN Service - Triggers local EPLAN automation via the eplan:// URI protocol.
 *
 * The LEA installer registers the eplan:// scheme in HKCU\Software\Classes\eplan,
 * pointing to eplan-handler.ps1 which calls W3u.exe /Auto.
 * The browser prompts the user once per origin to allow the protocol.
 */

/**
 * Trigger an EPLAN W3u.exe /Auto action via the eplan:// protocol handler.
 *
 * @param {string} action   - EPLAN action name, e.g. 'print', 'export', 'backup'
 * @param {string} project  - Full path to the .elk or .epj project file
 * @param {Object} [params] - Additional action-specific key/value parameters
 *                            forwarded as /KEY:VALUE to W3u.exe
 */
/**
 * Build the equivalent W3u.exe command line for display purposes.
 * Mirrors what eplan-handler.ps1 will execute.
 */
export function buildEplanCommand(action, project, params = {}) {
    const extraArgs = Object.entries(params)
        .filter(([k]) => k.toLowerCase() !== 'project')
        .map(([k, v]) => `/${k.toUpperCase()}:${v}`)
        .join(' ');
    const base = `W3u.exe /Auto ${action} /PROJECTNAME:"${project}"`;
    return extraArgs ? `${base} ${extraArgs}` : base;
}

export function triggerEplanAction(action, project, params = {}) {
    if (!action) throw new Error('Aktion ist erforderlich');
    if (!project) throw new Error('Projektpfad ist erforderlich');

    const query = new URLSearchParams({ project, ...params }).toString();

    // Dispatching a custom URI scheme never navigates away from the page.
    // The browser passes the URI to eplan-handler.ps1 via the registered handler.
    window.location.href = `eplan://${action}?${query}`;
}

/**
 * OpenAI tool definition for EPLAN automation.
 * Included in standalone-mode chat so the AI can trigger EPLAN actions.
 *
 * ══════════════════════════════════════════════════════════════════
 * ACTION: print
 * ══════════════════════════════════════════════════════════════════
 *   TYPE         PROJECT | PAGES
 *   PRINTERNAME  Druckername (optional, Standard: Windows-Standarddrucker)
 *   PAGENAME     Zu druckende Seite (optional)
 *   PAGENAMEn    Mehrere Seiten: PAGENAME1, PAGENAME2, … (optional)
 *   NUMBER       Anzahl der Ausdrucke (Standard: 1)
 *   DESTINATIONFILE  Ausgabedatei statt Drucker (optional)
 *   PRINTCOLLATE Sortiert: 0|1 (Standard: 1)
 *   PRINTREVERSE Umgekehrte Reihenfolge: 0|1 (Standard: 0)
 *   USEPAGEFILTER Nur gefilterte Seiten: 0|1 (Standard: 0)
 *   PRINTCHANGEDPAGES Nur geänderte Seiten drucken: 0|1
 *
 * ══════════════════════════════════════════════════════════════════
 * ACTION: export
 * ══════════════════════════════════════════════════════════════════
 *   TYPE         PXFPROJECT | PDFPROJECTSCHEME | PDFPAGESSCHEME |
 *                DXFPROJECT | DXFPAGE | DWGPROJECT | DWGPAGE |
 *                GRAPHICPROJECT | GRAPHICPAGE | PDFPROJECT (veraltet) |
 *                PDFPAGE (veraltet) | PDFPAGES (veraltet)
 *   EXPORTFILE   Ausgabedateiname (für PXFPROJECT, PDFPROJECTSCHEME, PDFPAGESSCHEME)
 *   DESTINATIONPATH  Zielverzeichnis (für Grafik-, DXF-, DWG-Export)
 *   EXPORTSCHEME Schema für DXF/DWG/PDF (optional)
 *   FORMAT       BMP | TIF | GIF | PNG | JPG (nur für GRAPHICPAGE/PROJECT, Standard: TIF)
 *   COLORDEPTH   1|8|16|24|32 (Standard: 24, nur Grafik)
 *   IMAGEWIDTH   Breite in Pixeln (Standard: 80, nur Grafik)
 *   BLACKWHITE   0=Farbe | 1=S/W | 2=Graustufen | 3=Weiß invertiert
 *   LANGUAGE     Ausgabesprache, z.B. en_US, de_DE (nur DXF/DWG)
 *   PAGENAME     Zu exportierende Seite (optional)
 *   PAGENAMEn    Mehrere Seiten: PAGENAME1, PAGENAME2, … (optional)
 *   USEPAGEFILTER Nur gefilterte Seiten: 0|1 (Standard: 0)
 *   EXPORTMASTERDATA Mit Stammdaten exportieren: 0|1 (nur PXFPROJECT, Standard: 1)
 *   EXPORTCONNECTIONS Mit Verbindungen exportieren: 0|1 (nur PXFPROJECT, Standard: 0)
 *   TARGET       Disk | FromSettings (nur DXF/DWG)
 *   EXPORTMODEL  3D-Modelle mit exportieren: 0|1 (nur PDFPROJECTSCHEME/PDFPAGESSCHEME)
 *   USEZOOM      Ausschnitt-Fenster in PDF: 0|1
 *   ZOOMLEVEL    Zoomstufe in mm (1–3500, nur wenn USEZOOM=1)
 *   USESIMPLELINK Einfache Verknüpfung in PDF: 0|1
 *   FASTWEBVIEW  Schnelle Web-Anzeige in PDF: 0|1
 *   READONLYEXPORT PDF schreibgeschützt: 0|1
 *   USEPRINTMARGINS Druckränder verwenden: 0|1 (Standard: 0)
 *
 * ══════════════════════════════════════════════════════════════════
 * ACTION: backup
 * ══════════════════════════════════════════════════════════════════
 *   TYPE         PROJECT | MASTERDATA
 *   ARCHIVENAME  Name der Sicherungsdatei (ohne Pfad)
 *   DESTINATIONPATH  Zielverzeichnis
 *   BACKUPMETHOD BACKUP | SOURCEOUT | ARCHIVE
 *   COMMENT      Kommentar zur Sicherung (optional)
 *   AUTOCOPYREFDATA Referenzierte Daten vorher kopieren: 0|1 (Standard: 0)
 *   INCLEXTDOCS  Fremddokumente einschließen: 0|1 (Standard: 0)
 *   INCLIMAGES   Bilddateien einschließen: 0|1 (Standard: 0)
 *   MDTYPE       Stammdatentyp (nur TYPE=MASTERDATA):
 *                SYMBOLS | MACROS | FORMS | ARTICLES | LANGUAGES | STANDARDSHEET | STATIONDATA
 *   SOURCEPATH   Quellverzeichnis (nur Stammdaten)
 *   FILENAME     Dateiname der zu sichernden Datei (nur Stammdaten, Wildcards erlaubt)
 *
 * ══════════════════════════════════════════════════════════════════
 * ACTION: restore
 * ══════════════════════════════════════════════════════════════════
 *   TYPE         PROJECT | MASTERDATA
 *   ARCHIVENAME  Name des wiederherzustellenden Archivs
 *   DESTINATIONPATH  Zielverzeichnis (nur TYPE=MASTERDATA)
 *   UNPACKPROJECT Gepacktes Projekt entpacken: 0|1 (Standard: 0, nur PROJECT)
 *   MODE         0=Standard | 1=ProjectInfo.xml berücksichtigen (nur PROJECT)
 *
 * ══════════════════════════════════════════════════════════════════
 * ACTION: check
 * ══════════════════════════════════════════════════════════════════
 *   TYPE         PROJECT | PAGES | INSTALLATIONSPACE | ONLYCOMPLETED | PARTS
 *   VERIFICATIONSCHEME  Prüfschema (optional, nur Schemaname ohne Pfad)
 *   PAGENAME     Zu prüfende Seite (optional)
 *   SELn         Objekt-ID der zu prüfenden Seiten (alternativ zu PAGENAME)
 *   USEPAGEFILTER Nur gefilterte Seiten: 0|1 (Standard: 0)
 *   INSTALLATIONSPACENAME  Name des Bauraums (nur TYPE=INSTALLATIONSPACE)
 *
 * ══════════════════════════════════════════════════════════════════
 * ACTION: reports
 * ══════════════════════════════════════════════════════════════════
 *   TYPE         PROJECT | PAGES | UPDATEMODELVIEWPAGES |
 *                CREATEMODELVIEWS | CREATECOPPERUNFOLDS | CREATEDRILLINGVIEWS
 *   PAGENAME     Zu aktualisierende Seite (optional, nur PAGES/UPDATEMODELVIEWPAGES)
 *   PAGENAMEn    Mehrere Seiten (optional)
 *   SELn         Objekt-ID der Seiten (alternativ zu PAGENAMEn)
 *   USEPAGEFILTER Nur gefilterte Seiten: 0|1 (Standard: 0)
 *   PAGEFILTERNAME  Name des Seitenfilters
 *   TEMPLATEn    Vorlagen für Ansichtserzeugung (TEMPLATE1, TEMPLATE2, …)
 *   REPLACEEXISTING Bestehende Ansichten ersetzen: 0|1
 *
 * ══════════════════════════════════════════════════════════════════
 * ACTION: translate
 * ══════════════════════════════════════════════════════════════════
 *   TYPE         TRANSLATEPROJECT | TRANSLATEPAGES | REMOVELANGUAGE |
 *                EXPORTMISSINGTRANSLATIONS | IMPORTTOTRANSDB | EXPORTFROMTRANSDB
 *   LANGUAGE     Übersetzungssprache, z.B. fr_FR, en_US
 *                (für REMOVELANGUAGE mehrere mit Komma: en_US,fr_FR)
 *   IMPORTFILE   Quelldatei (nur IMPORTTOTRANSDB)
 *   EXPORTFILE   Ausgabedatei Fehlwortliste (nur EXPORTMISSINGTRANSLATIONS)
 *   CONVERTER    Konvertername (optional)
 *   PAGENAME     Zu übersetzende Seite (optional, nur TRANSLATEPAGES)
 *   PAGENAMEn    Mehrere Seiten (optional)
 *   USEPAGEFILTER Nur gefilterte Seiten: 0|1 (Standard: 0, nur TRANSLATEPAGES)
 *   PAGEFILTERNAME  Name des Seitenfilters
 *
 * ══════════════════════════════════════════════════════════════════
 * Other actions (no required params beyond PROJECTNAME):
 *   compress, masterdata, projectmanagement, Renumber, synchronize,
 *   generate, generatemacros, subprojects, SetProjectLanguage,
 *   partslist, partsmanagementapi, devicelist, plcservice, label,
 *   import, XGedUpdateMacroAction, XMExportConnectionsAction,
 *   XMExportFunctionAction, XMExportPagesAction,
 *   XPamsDeviceSelectionAction, XPrjActionUpgradeProjects,
 *   XSettingsExport, XSettingsImport, GraphicalLayerTable,
 *   export3D, exportNCData, exportProductionWiring, exportToGraphics,
 *   changelayer, ProjectOpen
 */
export const EPLAN_TOOLS = [
    {
        type: 'function',
        function: {
            name: 'eplan_execute',
            description: `Führt eine EPLAN P8 Automatisierungsaufgabe über die Befehlszeile aus (W3u.exe /Auto). Nur verfügbar wenn LEA lokal auf dem EPLAN-Rechner installiert ist (eplan:// Protokoll-Handler).

STRUKTUR DES AUFRUFS – immer exakt so:
{
  "action": "<Aktionsname>",
  "project": "<vollständiger Pfad zur .elk/.epj/.edb-Datei>",
  "params": { "KEY": "VALUE", ... }
}

NIEMALS Parameter in den action-String einbauen (kein "export?TYPE=..." o.ä.)!
Alle aktionsspezifischen Parameter gehören AUSSCHLIESSLICH in das params-Objekt.
Das params-Objekt ist PFLICHT und muss mindestens TYPE enthalten.

Vollständige Beispiele:

PDF-Export:
{ "action": "export", "project": "H:\\\\Projekte\\\\Demo.edb", "params": { "TYPE": "PDFPROJECTSCHEME", "EXPORTFILE": "C:\\\\temp\\\\Demo.pdf" } }

PXF-Export:
{ "action": "export", "project": "H:\\\\Projekte\\\\Demo.edb", "params": { "TYPE": "PXFPROJECT", "EXPORTFILE": "C:\\\\temp\\\\Demo" } }

DXF-Export:
{ "action": "export", "project": "H:\\\\Projekte\\\\Demo.edb", "params": { "TYPE": "DXFPROJECT", "DESTINATIONPATH": "C:\\\\temp\\\\dxf" } }

Drucken:
{ "action": "print", "project": "H:\\\\Projekte\\\\Demo.edb", "params": { "TYPE": "PROJECT" } }

Backup:
{ "action": "backup", "project": "H:\\\\Projekte\\\\Demo.edb", "params": { "TYPE": "PROJECT", "ARCHIVENAME": "Demo.zw1", "DESTINATIONPATH": "C:\\\\backup", "BACKUPMETHOD": "BACKUP" } }

Verfügbare Aktionen: backup, changelayer, check, compress, devicelist, export, export3D, exportNCData, exportProductionWiring, exportToGraphics, generate, generatemacros, GraphicalLayerTable, import, label, masterdata, partslist, partsmanagementapi, plcservice, print, projectmanagement, ProjectOpen, Renumber, reports, restore, SetProjectLanguage, subprojects, synchronize, translate, XGedUpdateMacroAction, XMExportConnectionsAction, XMExportFunctionAction, XMExportPagesAction, XPamsDeviceSelectionAction, XPrjActionUpgradeProjects, XSettingsExport, XSettingsImport

Parameter je Aktion (alle in params):
• export – TYPE PFLICHT:
  PDFPROJECTSCHEME → EXPORTFILE (vollst. Pfad + .pdf); opt: EXPORTSCHEME, BLACKWHITE, EXPORTMODEL
  PXFPROJECT       → EXPORTFILE (Pfad ohne Erweiterung); opt: EXPORTMASTERDATA, EXPORTCONNECTIONS
  DXFPROJECT/DWGPROJECT → opt: DESTINATIONPATH, EXPORTSCHEME, LANGUAGE
  DXFPAGE/DWGPAGE  → PAGENAME oder PAGENAMEn; opt: DESTINATIONPATH
  GRAPHICPROJECT/GRAPHICPAGE → DESTINATIONPATH; opt: FORMAT (TIF|PNG|JPG|GIF|BMP), COLORDEPTH, BLACKWHITE
  PDFPAGESSCHEME   → EXPORTFILE (vollst. Pfad + .pdf); opt: PAGENAMEn, EXPORTSCHEME
• print  – TYPE=PROJECT|PAGES; opt: PRINTERNAME, PAGENAME, NUMBER, DESTINATIONFILE, USEPAGEFILTER
• backup – TYPE=PROJECT|MASTERDATA PFLICHT; ARCHIVENAME PFLICHT (NUR Dateiname OHNE Pfad, z.B. "Demo.zw1"); DESTINATIONPATH PFLICHT (Zielverzeichnis, z.B. "C:\\backup"); BACKUPMETHOD PFLICHT = BACKUP|SOURCEOUT|ARCHIVE (Standard: BACKUP)
  ACHTUNG: ARCHIVENAME enthält NUR den Dateinamen, KEINEN Verzeichnispfad!
  Beispiel: { "TYPE": "PROJECT", "ARCHIVENAME": "Demo.zw1", "DESTINATIONPATH": "C:\\backup", "BACKUPMETHOD": "BACKUP" }
• restore – TYPE=PROJECT|MASTERDATA; ARCHIVENAME (NUR Dateiname ohne Pfad); DESTINATIONPATH (Quellverzeichnis)
• check   – TYPE=PROJECT|PAGES|PARTS; opt: VERIFICATIONSCHEME, PAGENAME, USEPAGEFILTER
• reports – TYPE=PROJECT|PAGES; opt: PAGENAME, USEPAGEFILTER, PAGEFILTERNAME
• translate – TYPE=TRANSLATEPROJECT|TRANSLATEPAGES|REMOVELANGUAGE|EXPORTMISSINGTRANSLATIONS; opt: LANGUAGE, EXPORTFILE, IMPORTFILE

Fragt den Benutzer nach fehlenden Pflichtparametern (z.B. Zieldatei für Export), bevor das Tool aufgerufen wird.`,
            parameters: {
                type: 'object',
                properties: {
                    action: {
                        type: 'string',
                        description: 'Name der EPLAN-Aktion, z.B. "print", "export", "backup", "check", "reports", "translate" usw. Groß-/Kleinschreibung beachten bei X...-Aktionen.'
                    },
                    project: {
                        type: 'string',
                        description: 'Vollständiger Windows-Pfad zur EPLAN-Projektdatei (.elk, .epj oder .edb), z.B. "C:\\\\Users\\\\Public\\\\EPLAN\\\\Data\\\\Projects\\\\Demo.edb"'
                    },
                    params: {
                        type: 'object',
                        description: 'Aktionsspezifische Parameter als Schlüssel-Wert-Paare (PFLICHTFELD – darf nicht leer sein). Diese werden als /KEY:VALUE an W3u.exe übergeben. Für jede Aktion muss mindestens TYPE angegeben werden. Beispiele: print → { "TYPE": "PROJECT" }; export als PDF → { "TYPE": "PDFPROJECTSCHEME", "EXPORTFILE": "C:\\\\output\\\\plan.pdf" }; backup → { "TYPE": "PROJECT", "ARCHIVENAME": "C:\\\\backup\\\\projekt.zw1" }.',
                        additionalProperties: { type: 'string' }
                    }
                },
                required: ['action', 'project', 'params']
            }
        }
    }
];
